import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = [
    'createForm', 'outlineForm', 'outlineContainer',
    'submitBtn', 'loadingIndicator',
    'programName', 'comment', 'regenerateBtn', 'saveBtn', 'backBtn'
  ];
  static values = {
    aiOutlinesPath: String,
    savePath: String,
    unexpectedError: String,
    saveFailed: String
  };

  connect() {
    this.conversationHistory = [];
    this.currentOutline = null;
    this.accumulatedTokens = { input_tokens: 0, output_tokens: 0, total_tokens: 0 };
    this.closeTypeMenus = this.closeTypeMenus.bind(this);
    this._saveFormBound = this._saveFormValues.bind(this);
    document.addEventListener('click', this.closeTypeMenus);
    this._restoreFormValues();
    this._attachFormListeners();
  }

  disconnect() {
    document.removeEventListener('click', this.closeTypeMenus);
    this._detachFormListeners();
  }

  async submit(event) {
    event.preventDefault();

    this._setLoading(true);

    try {
      const { ok, data } = await this._fetchJSON(
        this.aiOutlinesPathValue,
        this._buildFormData()
      );
      if (ok) {
        this.currentOutline = data.outline;
        this._accumulateTokens(data.token_usage);
        this._clearFormValues();
        this._showOutlineForm(data.outline_html);
      } else {
        this._showError((data.errors || []).join(' '));
      }
    } catch {
      this._showError(this.unexpectedErrorValue);
    } finally {
      this._setLoading(false);
    }
  }

  async regenerate(event) {
    event.preventDefault();

    const comment = this.commentTarget.value.trim();
    if (!comment) return;

    this.conversationHistory.push({ outline: this.currentOutline, comment });
    this._setRegenerating(true);

    const formData = this._buildFormData();
    formData.delete('files[]');
    formData.set('conversation_history', JSON.stringify(this.conversationHistory));

    try {
      const { ok, data } = await this._fetchJSON(this.aiOutlinesPathValue, formData);
      if (ok) {
        this.currentOutline = data.outline;
        this._accumulateTokens(data.token_usage);
        this._updateOutline(data.outline_html);
        this.commentTarget.value = '';
        this._updateRegenerateBtn();
      } else {
        this.conversationHistory.pop();
        this._showError((data.errors || []).join(' '));
      }
    } catch {
      this.conversationHistory.pop();
      this._showError(this.unexpectedErrorValue);
    } finally {
      this._setRegenerating(false);
    }
  }

  async save(event) {
    event.preventDefault();

    const outline = {
      ...this.currentOutline,
      program_name: this.hasProgramNameTarget
        ? this.programNameTarget.value
        : this.currentOutline.program_name
    };

    this._setSaving(true);

    const body = new FormData();
    body.append('outline', JSON.stringify(outline));
    body.append('accumulated_tokens', JSON.stringify(this.accumulatedTokens));

    try {
      const { ok, data } = await this._fetchJSON(this.savePathValue, body);
      if (ok) {
        this._clearFormValues();
        window.location.href = data.redirect_url;
      } else {
        this._showError(data.error || this.saveFailedValue);
      }
    } catch {
      this._showError(this.unexpectedErrorValue);
    } finally {
      this._setSaving(false);
    }
  }

  commentTargetConnected() {
    this._updateRegenerateBtn();
  }

  onCommentInput() {
    this._updateRegenerateBtn();
  }

  back(event) {
    event.preventDefault();
    this.outlineFormTarget.classList.add('hidden');
    this.createFormTarget.classList.remove('hidden');
  }

  openRationale(event) {
    event.preventDefault();
    const panelEl = document.getElementById('rationale-panel');
    if (!panelEl) return;
    this.application
      .getControllerForElementAndIdentifier(panelEl, 'program-ai-outline-rationale')
      ?.open();
  }

  navigateBack(event) {
    event.preventDefault();
    history.back();
  }

  handleEdit(event) {
    if (!this.currentOutline) return;
    const el = event.currentTarget;
    const field = el.dataset.editField;
    const value = el.textContent.trim();

    if (!value) {
      if (field === 'module-title') {
        el.textContent = this.currentOutline.modules[parseInt(el.dataset.moduleIdx)].title;
      } else if (field === 'page-title') {
        el.textContent = this.currentOutline.modules[parseInt(el.dataset.moduleIdx)]
          .content_pages[parseInt(el.dataset.pageIdx)].title;
      } else if (field === 'task-title') {
        el.textContent = this.currentOutline.modules[parseInt(el.dataset.moduleIdx)]
          .content_pages[parseInt(el.dataset.pageIdx)].tasks[parseInt(el.dataset.taskIdx)].title;
      }
      return;
    }

    const moduleIdx = parseInt(el.dataset.moduleIdx);
    const pageIdx = parseInt(el.dataset.pageIdx);
    const taskIdx = parseInt(el.dataset.taskIdx);

    if (field === 'module-title') {
      this.currentOutline.modules[moduleIdx].title = value;
    } else if (field === 'page-title') {
      this.currentOutline.modules[moduleIdx].content_pages[pageIdx].title = value;
    } else if (field === 'task-title') {
      this.currentOutline.modules[moduleIdx].content_pages[pageIdx].tasks[taskIdx].title = value;
    }
  }

  handleTitleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  }

  toggleTypeMenu(event) {
    event.stopPropagation();
    const container = event.currentTarget.closest('[data-edit-field]');
    const menu = container.querySelector('[data-type-menu]');

    if (this.hasOutlineContainerTarget) {
      this.outlineContainerTarget.querySelectorAll('[data-type-menu]').forEach(m => {
        if (m !== menu) m.classList.add('hidden');
      });
    }

    menu.classList.toggle('hidden');
  }

  selectType(event) {
    if (!this.currentOutline) return;
    const value = event.currentTarget.dataset.value;
    const label = event.currentTarget.dataset.label || value;
    const color = event.currentTarget.dataset.color;
    const container = event.currentTarget.closest('[data-edit-field]');
    const field = container.dataset.editField;
    const moduleIdx = parseInt(container.dataset.moduleIdx);

    container.querySelector('[data-type-label]').textContent = label;

    if (color) {
      const btn = container.querySelector('button');
      btn.className = btn.className.replace(/bg-\w+-\d+/, color);
    }

    if (field === 'module-type') {
      this.currentOutline.modules[moduleIdx].module_type = value;
    } else if (field === 'page-type') {
      const pageIdx = parseInt(container.dataset.pageIdx);
      this.currentOutline.modules[moduleIdx]
        .content_pages[pageIdx].content_page_type = value;
    } else if (field === 'task-type') {
      const pageIdx = parseInt(container.dataset.pageIdx);
      const taskIdx = parseInt(container.dataset.taskIdx);
      this.currentOutline.modules[moduleIdx]
        .content_pages[pageIdx].tasks[taskIdx].task_type = value;
    }

    container.querySelector('[data-type-menu]').classList.add('hidden');
  }

  removeItem(event) {
    if (!this.currentOutline) return;
    const btn = event.currentTarget;
    const field = btn.dataset.removeField;
    const moduleIdx = parseInt(btn.dataset.moduleIdx);

    if (field === 'page') {
      const pageEl = btn.closest('[data-page-item]');
      const pageIdx = Array.from(
        pageEl.parentElement.querySelectorAll(':scope > [data-page-item]')
      ).indexOf(pageEl);
      this.currentOutline.modules[moduleIdx].content_pages.splice(pageIdx, 1);
      pageEl.remove();
    } else if (field === 'task') {
      const taskEl = btn.closest('[data-task-item]');
      const pageEl = taskEl.closest('[data-page-item]');
      const pageIdx = Array.from(
        pageEl.parentElement.querySelectorAll(':scope > [data-page-item]')
      ).indexOf(pageEl);
      const taskIdx = Array.from(
        taskEl.parentElement.querySelectorAll(':scope > [data-task-item]')
      ).indexOf(taskEl);
      this.currentOutline.modules[moduleIdx]
        .content_pages[pageIdx].tasks.splice(taskIdx, 1);
      taskEl.remove();
    }
  }

  closeTypeMenus() {
    if (!this.hasOutlineContainerTarget) return;
    this.outlineContainerTarget.querySelectorAll('[data-type-menu]').forEach(menu => {
      menu.classList.add('hidden');
    });
  }

  _updateRegenerateBtn() {
    if (this.hasRegenerateBtnTarget) {
      this.regenerateBtnTarget.disabled = !this.commentTarget.value.trim();
    }
  }

  _storageKey() {
    if (!this.hasCreateFormTarget) return null;
    const programType = this.createFormTarget.querySelector('input[name="program_type"]')?.value;
    return programType ? `ai_wizard_form_${programType}` : null;
  }

  _restoreFormValues() {
    const key = this._storageKey();
    if (!key) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (!saved) return;
      const form = this.createFormTarget.querySelector('form');
      if (!form) return;
      Object.entries(saved).forEach(([name, value]) => {
        const el = form.querySelector(`[name="${name}"]`);
        if (el) el.value = value;
      });
    } catch { /* ignore */ }
  }

  _saveFormValues() {
    const key = this._storageKey();
    if (!key) return;
    const form = this.createFormTarget.querySelector('form');
    if (!form) return;
    const values = {};
    form.querySelectorAll('input:not([type=hidden]):not([type=file]), textarea').forEach(el => {
      if (el.name) values[el.name] = el.value;
    });
    try { sessionStorage.setItem(key, JSON.stringify(values)); } catch { /* ignore */ }
  }

  _clearFormValues() {
    const key = this._storageKey();
    if (!key) return;
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
  }

  _attachFormListeners() {
    if (!this.hasCreateFormTarget) return;
    this.createFormTarget.addEventListener('input', this._saveFormBound);
  }

  _detachFormListeners() {
    if (!this.hasCreateFormTarget) return;
    this.createFormTarget.removeEventListener('input', this._saveFormBound);
  }

  _accumulateTokens(usage) {
    if (!usage) return;
    this.accumulatedTokens.input_tokens += (usage.input_tokens || 0);
    this.accumulatedTokens.output_tokens += (usage.output_tokens || 0);
    this.accumulatedTokens.total_tokens += (usage.total_tokens || 0);
  }

  _buildFormData() {
    const form = this.createFormTarget.querySelector('form');
    return new FormData(form);
  }

  _showOutlineForm(html) {
    this.outlineContainerTarget.innerHTML = html;
    this.createFormTarget.classList.add('hidden');
    this.outlineFormTarget.classList.remove('hidden');
    this.outlineFormTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _updateOutline(html) {
    this.outlineContainerTarget.innerHTML = html;
  }

  async _fetchJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      body
    });
    const data = await response.json();
    return { ok: response.ok, data };
  }

  _setLoading(loading) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle('hidden', !loading);
    }
    if (this.hasCreateFormTarget) {
      if (loading) {
        this.createFormTarget.classList.add('hidden');
      } else {
        const step2Showing = this.hasOutlineFormTarget &&
          !this.outlineFormTarget.classList.contains('hidden');
        if (!step2Showing) this.createFormTarget.classList.remove('hidden');
      }
    }
  }

  _setRegenerating(loading) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle('hidden', !loading);
    }
    if (this.hasOutlineFormTarget) {
      if (loading) {
        this.outlineFormTarget.classList.add('hidden');
      } else {
        this.outlineFormTarget.classList.remove('hidden');
        this.outlineFormTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  _setSaving(loading) {
    if (this.hasSaveBtnTarget) this.saveBtnTarget.disabled = loading;
    if (this.hasRegenerateBtnTarget) this.regenerateBtnTarget.disabled = loading;
    if (this.hasBackBtnTarget) this.backBtnTarget.disabled = loading;
  }

  _showError(message) {
    document.dispatchEvent(
      new CustomEvent('promote:display-error-message', { detail: { message } })
    );
  }
}
